import { useQuery } from "@apollo/client";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { CloseOutlined } from "@mui/icons-material";
import { Grid, IconButton, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { SingleBorderCard } from "../../common/SingleBorderCard";
import { listRewards } from "./listRewards";
import "./rewardSelect.scss";

const RewardSelectComponent = ({ data, path, handleChange, uischema }) => {
  const multiple = uischema?.options?.multiple || false;
  const [selectedRewards, setSelectedRewards] = useState(multiple ? [] : null);
  const [rewardList, setRewardList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: rewardData, loading } = useQuery(listRewards, {
    variables: { filter: {} },
  });

  useEffect(() => {
    if (!rewardData?.listRewards?.items) return;
    const allRewards = rewardData.listRewards.items;
    setRewardList(allRewards);

    if (data) {
      if (multiple) {
        const ids = data.map((d) => (typeof d === "string" ? d : d.id));
        const matched = allRewards.filter((r) => ids.includes(r.id));
        setSelectedRewards(matched);
      } else {
        const id = typeof data === "string" ? data : data?.id;
        const matched = allRewards.find((r) => r.id === id) || null;
        setSelectedRewards(matched);
      }
    } else {
      setSelectedRewards(multiple ? [] : null);
    }
  }, [rewardData, data, multiple]);

  const filteredRewards = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return rewardList.filter(
      (reward) =>
        reward.name?.toLowerCase().includes(term) ||
        reward.shortDescription?.toLowerCase().includes(term)
    );
  }, [rewardList, searchTerm]);

  const isRewardSelected = (reward) => {
    return multiple
      ? selectedRewards.some((r) => r.id === reward.id)
      : selectedRewards?.id === reward.id;
  };

  const onRewardSelect = (reward) => {
    if (multiple) {
      if (isRewardSelected(reward)) return;
      const updated = [...selectedRewards, reward];
      setSelectedRewards(updated);
      handleChange(
        path,
        updated.map((r) => r.id)
      );
    } else {
      setSelectedRewards(reward);
      handleChange(path, reward.id);
    }
  };

  const onRemoveReward = (reward) => {
    if (multiple) {
      const updated = selectedRewards.filter((r) => r.id !== reward.id);
      setSelectedRewards(updated);
      handleChange(
        path,
        updated.map((r) => r.id)
      );
    } else {
      setSelectedRewards(null);
      handleChange(path, null);
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          variant="outlined"
          label="Search Rewards"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Grid container spacing={2}>
          {!loading && filteredRewards.length === 0 && (
            <Grid item xs={12} className="text-center">
              <Typography>No rewards found</Typography>
            </Grid>
          )}
          {filteredRewards.map((reward) => (
            <Grid
              item
              xs={6}
              md={4}
              xl={3}
              key={reward.id}
              className="cursor-pointer"
              onClick={() => onRewardSelect(reward)}
            >
              <SingleBorderCard>
                <img
                  src={reward.imageUrl}
                  height={200}
                  style={{
                    borderRadius: "6px",
                    objectFit: "cover",
                    width: "100%",
                  }}
                  alt={reward.name}
                />
                <Typography
                  className="font-sm text-wrap text-ellipsis-2"
                  style={{ minHeight: "35px" }}
                  title={reward.name}
                >
                  {reward.name}
                </Typography>
                <Typography
                  className="font-sm text-wrap text-ellipsis-2"
                  style={{ minHeight: "35px", color: "#616161" }}
                  title={reward.shortDescription}
                >
                  {reward.shortDescription}
                </Typography>
                {isRewardSelected(reward) && (
                  <img
                    src={"/assets/images/checkmark.png"}
                    height={24}
                    width={24}
                    style={{ position: "absolute", right: 10, top: 10 }}
                    alt="Selected"
                  />
                )}
              </SingleBorderCard>
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid item xs={12} md={4}>
        <Typography variant="h6" mb={4}>
          Selected Reward{multiple ? "s" : ""}
        </Typography>
        <Grid container spacing={2}>
          {multiple
            ? selectedRewards.map((reward) => (
                <Grid item xs={12} key={reward.id}>
                  <div className="selected-reward">
                    <div>
                      <h5>{reward.name}</h5>
                      <p>{reward.shortDescription}</p>
                    </div>
                    <div>
                      <IconButton onClick={() => onRemoveReward(reward)}>
                        <CloseOutlined aria-label="Remove Reward" />
                      </IconButton>
                    </div>
                  </div>
                </Grid>
              ))
            : selectedRewards && (
                <Grid item xs={12}>
                  <div className="selected-reward">
                    <div>
                      <h5>{selectedRewards.name}</h5>
                      <p>{selectedRewards.shortDescription}</p>
                    </div>
                    <div>
                      <IconButton onClick={() => onRemoveReward(selectedRewards)}>
                        <CloseOutlined aria-label="Remove Reward" />
                      </IconButton>
                    </div>
                  </div>
                </Grid>
              )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export { RewardSelectComponent };
export const RewardSelectControl = withJsonFormsControlProps(RewardSelectComponent);
